import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import customerJourneyService from '../services/customerJourneyService';
import authService from '../services/authService';
import targetMarketService from '../services/targetMarketService';
import { TextWithTooltips } from '../components/Tooltip';
import BackToDashboard from '../components/BackToDashboard';

function CustomerJourney() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('suggest'); // suggest, select, generating, review, complete
  const [numberOfPersonas, setNumberOfPersonas] = useState(1);
  const [personaDescriptions, setPersonaDescriptions] = useState(['']);
  const [personasText, setPersonasText] = useState('');
  const [editedPersonasText, setEditedPersonasText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [targetMarketData, setTargetMarketData] = useState(null);
  const [suggestedPersonas, setSuggestedPersonas] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [customPersona, setCustomPersona] = useState('');
  const [reviewMode, setReviewMode] = useState('preview');

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if target market is completed
    const targetResult = await targetMarketService.getPrimaryNiche();
    if (!targetResult.success || !targetResult.data.primary_niche) {
      setError('Please complete Target Market Discovery first');
      return;
    }

    setTargetMarket(targetResult.data.primary_niche.niche_name);
    setTargetMarketData(targetResult.data.primary_niche);

    // Load existing personas if any
    const result = await customerJourneyService.getPersonas();
    if (result.success && result.data.personas_text) {
      setPersonasText(result.data.personas_text);
      setEditedPersonasText(result.data.personas_text);
      setNumberOfPersonas(result.data.number_of_personas || 1);
      setStage('complete');
    } else {
      // Fetch AI suggestions for personas
      fetchPersonaSuggestions();
    }
  };

  const fetchPersonaSuggestions = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/customer-journey/suggest-personas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestedPersonas(data.personas || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get suggestions');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }

    setLoading(false);
  };

  const handleSuggestionToggle = (index) => {
    // Single select - only one primary persona at a time
    if (selectedSuggestions.includes(index)) {
      setSelectedSuggestions([]);
    } else {
      setSelectedSuggestions([index]);
    }
  };

  const handleAddCustomPersona = () => {
    if (customPersona.trim() && suggestedPersonas.length < 10) {
      setSuggestedPersonas([...suggestedPersonas, {
        name: customPersona.trim(),
        description: 'Custom persona',
        is_custom: true
      }]);
      setSelectedSuggestions([...selectedSuggestions, suggestedPersonas.length]);
      setCustomPersona('');
    }
  };

  const handleProceedWithSelected = async () => {
    if (selectedSuggestions.length !== 1) return;

    const primaryIndex = selectedSuggestions[0];
    const primaryPersona = suggestedPersonas[primaryIndex];

    // Save ALL personas to database for future use, mark one as primary
    try {
      await fetch('/api/customer-journey/save-persona-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          personas: suggestedPersonas,
          primary_index: primaryIndex
        })
      });
    } catch (err) {
      console.error('Failed to save persona options:', err);
      // Continue anyway - not critical
    }

    // Generate journey map for the ONE selected persona
    setNumberOfPersonas(1);
    setPersonaDescriptions([primaryPersona.name]);
    setStage('generating');
    generateFromSelected([primaryPersona]);
  };

  const generateFromSelected = async (selectedPersonas) => {
    setLoading(true);
    setError('');

    const result = await customerJourneyService.generatePersonas(
      selectedPersonas.length,
      selectedPersonas.map(p => p.name)
    );

    setLoading(false);

    if (result.success) {
      setPersonasText(result.data.personas_text);
      setEditedPersonasText(result.data.personas_text);
      setStage('review');
    } else {
      setError(result.error);
      setStage('suggest');
    }
  };

  const handleGenerate = async () => {
    // Validate that all persona descriptions are filled in
    const emptyDescriptions = personaDescriptions.slice(0, numberOfPersonas).filter(d => !d.trim());
    if (emptyDescriptions.length > 0) {
      setError('Please describe all personas before generating');
      return;
    }

    setLoading(true);
    setError('');
    setStage('generating');

    const result = await customerJourneyService.generatePersonas(
      numberOfPersonas,
      personaDescriptions.slice(0, numberOfPersonas)
    );

    setLoading(false);

    if (result.success) {
      setPersonasText(result.data.personas_text);
      setEditedPersonasText(result.data.personas_text);
      setStage('review');
    } else {
      setError(result.error);
      setStage('select');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    const result = await customerJourneyService.savePersonas(editedPersonasText, numberOfPersonas);

    setLoading(false);

    if (result.success) {
      setPersonasText(editedPersonasText);
      setStage('complete');
    } else {
      setError(result.error);
    }
  };

  const handleRegenerate = () => {
    setStage('suggest');
    setPersonasText('');
    setEditedPersonasText('');
    setSuggestedPersonas([]);
    setSelectedSuggestions([]);
    fetchPersonaSuggestions();
  };

  const handleNumberOfPersonasChange = (num) => {
    setNumberOfPersonas(num);
    // Ensure we have enough slots in the descriptions array
    const newDescriptions = [...personaDescriptions];
    while (newDescriptions.length < num) {
      newDescriptions.push('');
    }
    setPersonaDescriptions(newDescriptions);
  };

  const handlePersonaDescriptionChange = (index, value) => {
    const newDescriptions = [...personaDescriptions];
    newDescriptions[index] = value;
    setPersonaDescriptions(newDescriptions);
  };

  const renderSuggest = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Customer Personas</h2>

        <p className="text-muted mb-4">
          Within your <TextWithTooltips text="target market" /> (<strong>{targetMarket}</strong>),
          there may be distinct personas - people with the same core need but different
          contexts, motivations, or situations.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info" className="mb-4">
          <strong>What is a persona?</strong><br />
          A persona represents a distinct type of customer. For example,
          within "Stressed B2B founders" you might have: bootstrapped solo founders vs.
          funded startup CEOs vs. agency owners. Each has different concerns and language.
        </Alert>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Analyzing your target market for potential personas...</p>
          </div>
        ) : suggestedPersonas.length > 0 ? (
          <>
            <h5 className="mb-3">Which <TextWithTooltips text="persona" /> should you target first?</h5>
            <p className="text-muted mb-3">
              Select your <strong><TextWithTooltips text="primary persona" /></strong> to start with. All personas will be saved for future expansion.
            </p>

            {suggestedPersonas.map((persona, index) => (
              <Card
                key={index}
                className={`mb-3 ${selectedSuggestions.includes(index) ? 'border-primary border-2 bg-light' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSuggestionToggle(index)}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-start">
                    <Form.Check
                      type="radio"
                      checked={selectedSuggestions.includes(index)}
                      onChange={() => {}}
                      className="me-3 mt-1"
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="fs-5">{persona.name}</strong>
                        {persona.pain_intensity && (
                          <span className={`badge ${persona.pain_intensity >= 8 ? 'bg-danger' : persona.pain_intensity >= 6 ? 'bg-warning' : 'bg-secondary'}`}>
                            <TextWithTooltips text="Pain Intensity" />: {persona.pain_intensity}/10
                          </span>
                        )}
                      </div>

                      {persona.description && persona.description !== 'Custom persona' && (
                        <p className="text-muted mb-2">{persona.description}</p>
                      )}

                      {/* Strategic Analysis */}
                      {persona.decision_authority && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className="badge bg-light text-dark border">
                            <TextWithTooltips text="Decision Authority" />: {persona.decision_authority}
                          </span>
                          <span className="badge bg-light text-dark border">
                            <TextWithTooltips text="Market Size" />: {persona.market_size}
                          </span>
                          <span className="badge bg-light text-dark border">
                            <TextWithTooltips text="CAC" />: {persona.acquisition_cost}
                          </span>
                          <span className="badge bg-light text-dark border">
                            <TextWithTooltips text="LTV" />: {persona.ltv_potential}
                          </span>
                        </div>
                      )}

                      {persona.recommendation && (
                        <Alert variant={persona.recommendation.toLowerCase().includes('great') || persona.recommendation.toLowerCase().includes('ideal') ? 'success' : 'info'} className="mb-0 py-1 px-2">
                          <small><strong>Analysis:</strong> {persona.recommendation}</small>
                        </Alert>
                      )}

                      {persona.is_custom && (
                        <span className="badge bg-secondary">Custom</span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}

            <div className="mt-3 mb-4">
              <Form.Label><strong>Don't see one that fits? Add your own:</strong></Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="e.g., Part-time consultants with day jobs"
                  value={customPersona}
                  onChange={(e) => setCustomPersona(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomPersona()}
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAddCustomPersona}
                  disabled={!customPersona.trim()}
                >
                  Add
                </Button>
              </div>
            </div>

            <Alert variant="warning" className="mb-3">
              <strong>Before you choose, consider your existing proof:</strong>
              <ul className="mb-0 mt-2">
                <li><strong>Testimonials:</strong> Which persona do your current testimonials represent?</li>
                <li><strong>Case studies:</strong> Who are the success stories you can tell in detail?</li>
                <li><strong>Results data:</strong> Which type of customer has documented wins?</li>
              </ul>
              <p className="mb-0 mt-2">
                <em>The "best" persona on paper may not be your best STARTING point if you lack proof for them.
                Start where you have evidence, then expand.</em>
              </p>
            </Alert>

            <Alert variant="secondary" className="mb-4">
              <small>
                <strong>Note:</strong> All {suggestedPersonas.length} personas will be saved. You can create journey maps for the others later as you expand your marketing.
              </small>
            </Alert>

            <div className="d-flex justify-content-between mt-4">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/dashboard/foundation')}
              >
                Save for Later
              </Button>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setStage('select')}
                >
                  Skip & Enter Manually
                </Button>
                <Button
                  variant="primary"
                  onClick={handleProceedWithSelected}
                  disabled={selectedSuggestions.length !== 1}
                >
                  {selectedSuggestions.length === 0
                    ? 'Select your primary persona'
                    : `Generate Journey for "${suggestedPersonas[selectedSuggestions[0]]?.name}"`}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p>No suggestions available. You can enter personas manually.</p>
            <Button variant="primary" onClick={() => setStage('select')}>
              Enter Manually
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const renderSelect = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Customer Journey & Personas</h2>

        <p className="text-muted mb-4">
          Within your primary target market (<strong>{targetMarket}</strong>),
          there may be distinct personas - people with the same core need but different
          contexts, concerns, and questions at each stage.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Alert variant="info">
          <strong>What is a persona?</strong><br />
          A persona represents a distinct type of customer within your niche. For example,
          within 'Remote Workers,' you might have: freelancers vs. corporate employees vs.
          entrepreneurs. Each has different concerns and language.
        </Alert>

        <Form.Group className="mb-4">
          <Form.Label><strong>How many distinct personas exist within your target market?</strong></Form.Label>

          <Form.Check
            type="radio"
            id="personas-1"
            name="personas"
            label="Single Persona (One main ideal customer - keeps marketing focused and simple)"
            checked={numberOfPersonas === 1}
            onChange={() => handleNumberOfPersonasChange(1)}
            className="mb-2"
          />

          <Form.Check
            type="radio"
            id="personas-2-3"
            name="personas"
            label="2-3 Personas (Your niche includes distinctly different customer types)"
            checked={numberOfPersonas === 2 || numberOfPersonas === 3}
            onChange={() => handleNumberOfPersonasChange(2)}
            className="mb-2"
          />

          {(numberOfPersonas === 2 || numberOfPersonas === 3) && (
            <Form.Group className="ms-4 mb-2">
              <Form.Label>Exact number:</Form.Label>
              <Form.Select
                value={numberOfPersonas}
                onChange={(e) => handleNumberOfPersonasChange(parseInt(e.target.value))}
                style={{ width: '100px' }}
              >
                <option value={2}>2</option>
                <option value={3}>3</option>
              </Form.Select>
            </Form.Group>
          )}

          <Form.Check
            type="radio"
            id="personas-4"
            name="personas"
            label="4 Personas (Complex niche with multiple segments - use sparingly)"
            checked={numberOfPersonas === 4}
            onChange={() => handleNumberOfPersonasChange(4)}
            className="mb-2"
          />

          <Form.Text className="text-muted d-block mt-3">
            <strong>Recommendation:</strong> Start with 1-2 personas. You can always add more later.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label><strong>Describe each persona:</strong></Form.Label>
          <Alert variant="info" className="mb-3">
            <strong>Be specific about their role, context, or situation.</strong>
            <br /><br />
            <strong>Good examples:</strong>
            <ul className="mb-2">
              <li>Corporate project managers in tech companies</li>
              <li>Freelance WordPress developers</li>
              <li>Solo e-commerce store owners</li>
            </ul>
            <strong>Avoid vague descriptions:</strong>
            <ul className="mb-0">
              <li>"Business people" (too broad)</li>
              <li>"Anyone who needs help" (not specific enough)</li>
            </ul>
          </Alert>

          {Array.from({ length: numberOfPersonas }, (_, i) => (
            <Form.Group key={i} className="mb-3">
              <Form.Label>Persona {i + 1}:</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Corporate project managers in tech companies"
                value={personaDescriptions[i] || ''}
                onChange={(e) => handlePersonaDescriptionChange(i, e.target.value)}
                maxLength={200}
              />
            </Form.Group>
          ))}
        </Form.Group>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/dashboard/foundation')}
          >
            Save for Later
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Personas'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderGenerating = () => (
    <Card className="shadow">
      <Card.Body className="p-5 text-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h4>Creating your customer personas and journey maps...</h4>
        <p className="text-muted">
          This may take 60-120 seconds depending on the number of personas.
        </p>
      </Card.Body>
    </Card>
  );

  const renderReview = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Review Your Customer Persona</h2>

        <p className="text-muted mb-4">
          Review your <TextWithTooltips text="customer persona" /> and their 4-stage <TextWithTooltips text="journey map" />:{' '}
          <TextWithTooltips text="Problem Aware" /> → <TextWithTooltips text="Solution Aware" /> → <TextWithTooltips text="Decision Ready" /> → <TextWithTooltips text="Customer Success" />.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="mb-3">
          <Button
            variant={reviewMode === 'preview' ? 'primary' : 'outline-primary'}
            size="sm"
            className="me-2"
            onClick={() => setReviewMode('preview')}
          >
            Preview
          </Button>
          <Button
            variant={reviewMode === 'edit' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setReviewMode('edit')}
          >
            Edit Markdown
          </Button>
        </div>

        {reviewMode === 'preview' ? (
          <div style={{
            maxHeight: '500px',
            overflowY: 'auto',
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '5px',
            marginBottom: '1rem'
          }} className="markdown-content">
            <ReactMarkdown>{editedPersonasText}</ReactMarkdown>
          </div>
        ) : (
          <Form.Group className="mb-4">
            <Form.Control
              as="textarea"
              rows={20}
              value={editedPersonasText}
              onChange={(e) => setEditedPersonasText(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
            />
            <Form.Text className="text-muted">
              Edit the markdown directly. Switch to Preview to see formatted result.
            </Form.Text>
          </Form.Group>
        )}

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={handleRegenerate}
            disabled={loading}
          >
            Start Over
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderComplete = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Customer Journey Complete</h2>

        <Alert variant="success">
          Your customer personas and journey maps have been saved!
        </Alert>

        <Card className="mb-4 border-success">
          <Card.Body>
            <h5 className="mb-3">Your Customer Personas for: {targetMarket}</h5>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              backgroundColor: '#f8f9fa',
              padding: '20px',
              borderRadius: '5px'
            }} className="markdown-content">
              <ReactMarkdown>{personasText}</ReactMarkdown>
            </div>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate('/dashboard/foundation')}>
            Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => setStage('review')}>
            Edit Personas
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {stage === 'suggest' && renderSuggest()}
          {stage === 'select' && renderSelect()}
          {stage === 'generating' && renderGenerating()}
          {stage === 'review' && renderReview()}
          {stage === 'complete' && renderComplete()}
        </Col>
      </Row>
    </Container>
  );
}

export default CustomerJourney;
